import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Coins } from 'lucide-react';
import { selectIsAuthenticated } from '../features/auth/authSlice';
import { getDashboardStats } from '../features/contentCompliance/contentComplianceSlice';
import { selectCreditBalance, selectEnforcementMode } from '../features/billing/billingSlice';
import { LOW_BALANCE_CREDITS } from '../features/billing/constants';
import { track } from '../analytics/analytics';
import './CreditBalancePill.css';

// Always-visible credit balance in the dashboard header. Clicking it goes to
// the pricing page to top up. Balance is hydrated by the dashboard/stats fetch
// (shared with DashboardPage) and kept live off scan responses.
function CreditBalancePill() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const balance = useSelector(selectCreditBalance);
  const enforcementMode = useSelector(selectEnforcementMode);
  const statsStatus = useSelector((state) => state.contentCompliance.dashboardStatsStatus);

  // Make sure we have a balance even when the user never opens the dashboard
  // index (e.g. lands straight on /dashboard/new-check).
  useEffect(() => {
    if (isAuthenticated && statsStatus === 'idle') {
      dispatch(getDashboardStats());
    }
  }, [isAuthenticated, statsStatus, dispatch]);

  // Hidden until credits are actually charged. While enforcement is "off"
  // scans are free, so a balance would be meaningless and the pill would link
  // to a purchase page for something the user has no use for yet. Matches the
  // sidebar credits box and the pre-scan cost hints.
  if (!isAuthenticated || enforcementMode === 'off') return null;

  const label = balance == null ? '—' : balance.toLocaleString();
  const low = balance != null && balance < LOW_BALANCE_CREDITS;

  return (
    <button
      className={`credit-pill${low ? ' low' : ''}`}
      onClick={() => { track('add_credits_clicked', { source: 'header_pill', low_balance: low }); navigate('/pricing'); }}
      title={low
        ? 'Credits running low — click to top up'
        : 'Your credit balance — click to add more'}
      aria-label={`Credit balance: ${label}${low ? ', running low' : ''}. Add credits.`}
    >
      <Coins size={15} className="credit-pill-icon" aria-hidden="true" />
      <span className="credit-pill-balance">{label}</span>
      <span className="credit-pill-label">credits</span>
    </button>
  );
}

export default CreditBalancePill;
